import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import storageApi from '../services/firebaseStorage'
import NotificationBoxPage from '../components/notificationBoxPage'
import {
  KEY_SUPPORTED_FORMATS,
  MAX_FILE_SIZE,
} from '../utilities/stringUtilities'
import apiTalentPool from '../services/api/talentPool'
import LoadingPage from '../components/loadingPage'
import TalentPoolInfo from '../components/talentPool/TalentPoolInfo'
import TalentPoolForm from '../components/talentPool/TalentPoolForm'
import theme from '../constants/theme'

function TalentPool() {
  const { invitationLink } = useParams()
  const { t } = useTranslation()
  const [currentView, setCurrentView] = useState('loading')
  const [talentPool, setTalentPool] = useState(null)
  const [company, setCompany] = useState(null)
  const [message, setMessage] = useState('')
  const progressRef = useRef(null)
  const errorRef = useRef(null)

  const PHONE_REG_EXP =
    /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/

  useEffect(() => {
    const fetchTalentPoolData = async () => {
      try {
        const response = await apiTalentPool.getByInvitationLink(invitationLink)
        setTalentPool(response.talentPool)

        const enhancedCompany = {
          companyName: response.talentPool.companyName,
          ...response.talentPool.company,
        }
        setCompany(enhancedCompany)
        setCurrentView('info')
      } catch {
        setMessage(t('pages.talentPoolNotFound'))
        setCurrentView('error')
      }
    }

    fetchTalentPoolData()
  }, [invitationLink, t])

  const handleApplyClick = () => {
    setCurrentView('form')
  }

  const handleBackClick = () => {
    setCurrentView('info')
  }

  const handleUploadProgress = (node, progress) => {
    const progressNode = node
    progressNode.setAttribute('aria-valuenow', progress)
    progressNode.setAttribute(
      'style',
      `width: ${progress}%; background-color: ${theme.talentinoBlue}`,
    )
    progressNode.innerText = `${progress}%`
  }

  const handleSuccessfulUpload = async (data, setIsLoading) => {
    handleUploadProgress(progressRef.current, 100)

    setTimeout(async () => {
      try {
        const response = await apiTalentPool.applyToTalentPool(invitationLink, {
          ...data,
          source: 'applyToTalentPool',
        })

        if (response.status === 'duplicate') {
          setCurrentView('error')
          setMessage(response.message)
        } else {
          setCurrentView('success')
          setMessage(
            t('pages.talentPoolApplySuccess', { talentPool: talentPool.name }),
          )
        }
      } catch {
        setCurrentView('error')
        setMessage(t('pages.talentPoolApplyError'))
      } finally {
        setIsLoading(false)
      }
    }, 500)
  }

  const handleUploadError = (error) => {
    errorRef.current.innerText = error
  }

  const handleSubmit = async (data, setIsLoading) => {
    if (!data.resumes.length) {
      // eslint-disable-next-line no-alert
      alert(t('validation.resumes'))
      setIsLoading(false)
      return
    }

    storageApi.uploadFile(
      data.resumes[0],
      (progress) => handleUploadProgress(progressRef.current, progress),
      (url) =>
        handleSuccessfulUpload(
          {
            ...data,
            resumes: [url],
            source: 'talent_pool_external',
          },
          setIsLoading,
        ),
      (err) => handleUploadError(err),
    )
  }

  const formInputs = [
    {
      name: 'firstName',
      label: t('form.firstName'),
      type: 'text',
      required: true,
      width: 6,
    },
    {
      name: 'lastName',
      label: t('form.lastName'),
      type: 'text',
      required: true,
      width: 6,
    },
    {
      name: 'email',
      label: t('form.email'),
      type: 'email',
      required: true,
      width: 6,
    },
    {
      name: 'phone',
      label: t('form.phone'),
      type: 'number',
      required: true,
      width: 6,
    },
    {
      name: 'address',
      label: t('form.address'),
      type: 'text',
      placeholder: t('form.addressPlaceholder'),
      required: true,
      width: 12,
    },
    {
      name: 'title',
      label: t('form.title'),
      type: 'text',
      required: true,
      width: 6,
    },
    {
      name: 'experienceYears',
      type: 'number',
      label: t('form.yearsOfExperience'),
      required: true,
      width: 6,
    },
    {
      name: 'resumes',
      label: t('form.resumes'),
      renderType: 'files',
      required: true,
      width: 12,
      accept: '.doc*, .dot*, .pdf',
    },
  ]

  const actions = [
    {
      text: t('general.back'),
      onClick: handleBackClick,
    },
    {
      field: 'submit',
      text: t('general.submit'),
      onClick: handleSubmit,
    },
  ]

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(t('general.required')),
    lastName: Yup.string().required(t('general.required')),
    email: Yup.string().email().required(t('general.required')),
    phone: Yup.string()
      .matches(PHONE_REG_EXP, `${t('validation.phone')}`)
      .required(t('general.required')),
    title: Yup.string().required(t('general.required')),
    address: Yup.string().required(t('general.required')),
    experienceYears: Yup.number(t('validation.number'))
      .typeError(t('validation.number'))
      .integer(t('validation.integer'))
      .min(0, t('validation.positive'))
      .required(t('general.required')),
    resumes: Yup.mixed()
      .required(t('general.required'))
      .test(
        'fileUpload',
        t('general.required'),
        (value) => Object.keys(value).length,
      )
      .test(
        'fileUpload',
        t('validation.fileNumber'),
        (value) => Object.keys(value).length === 1,
      )
      .test(
        'fileSize',
        t('validation.fileSize'),
        (files) =>
          Object.keys(files).filter((file) => files[file].size <= MAX_FILE_SIZE)
            .length,
      )
      .test(
        'fileFormat',
        t('validation.fileFormate'),
        (files) =>
          Object.keys(files).filter(
            (file) => files[file].type === KEY_SUPPORTED_FORMATS.pdf,
          ).length,
      ),
  })

  if (currentView === 'loading') {
    return <LoadingPage />
  }

  if (currentView === 'error') {
    return <NotificationBoxPage messageType="error" message={message} />
  }

  if (currentView === 'success') {
    return <NotificationBoxPage messageType="success" message={message} />
  }

  if (currentView === 'info') {
    return (
      <TalentPoolInfo
        talentPool={talentPool}
        company={company}
        onApplyClick={handleApplyClick}
      />
    )
  }

  if (currentView === 'form') {
    return (
      <TalentPoolForm
        talentPool={talentPool}
        company={company}
        formInputs={formInputs}
        validationSchema={validationSchema}
        actions={actions}
        defaultValues={{}}
        progressRef={progressRef}
        errorRef={errorRef}
      />
    )
  }

  return null
}

export default TalentPool
